const CHART_TYPES = new Set([
  'line',
  'bar',
  'pie',
  'area',
  'stacked-bar',
  'horizontal-bar',
  'scatter',
  'histogram',
  'combo',
  'heatmap',
  'radar',
]);
const MAX_POINTS = 100;
const MAX_SERIES = 6;

const asTrimmedString = (value, maxLength = 160) => (
  typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
);

export function parseChartSpec(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const type = CHART_TYPES.has(parsed.type) ? parsed.type : 'line';
    if (!Array.isArray(parsed.series) || parsed.series.length === 0 || parsed.series.length > MAX_SERIES) {
      return null;
    }

    const series = parsed.series.map((item, seriesIndex) => {
      if (!item || typeof item !== 'object' || !Array.isArray(item.data) || item.data.length === 0 || item.data.length > MAX_POINTS) {
        return null;
      }

      const data = item.data.map((point) => {
        if (!point || typeof point !== 'object') return null;
        const x = typeof point.x === 'number' || typeof point.x === 'string'
          ? String(point.x).slice(0, 48)
          : '';
        return x && typeof point.y === 'number' && Number.isFinite(point.y)
          ? {
            x,
            y: point.y,
            color: /^#[0-9a-f]{6}$/i.test(point.color || '') ? point.color : null,
          }
          : null;
      });

      if (data.some(point => point === null)) return null;

      return {
        name: asTrimmedString(item.name, 60) || `Series ${seriesIndex + 1}`,
        color: /^#[0-9a-f]{6}$/i.test(item.color || '') ? item.color : null,
        ...(type === 'combo' && {
          chartType: ['line', 'bar'].includes(item.chartType) ? item.chartType : (seriesIndex === 0 ? 'bar' : 'line'),
        }),
        data,
      };
    });

    if (series.some(item => item === null)) return null;
    if (type === 'pie') {
      if (series.length !== 1 || series[0].data.length > 12) return null;
      if (series[0].data.some(point => point.y < 0) || series[0].data.every(point => point.y === 0)) return null;
    }
    if (type === 'histogram' && series.length !== 1) return null;
    if (type === 'scatter' && series.some(item => item.data.some(point => !Number.isFinite(Number(point.x))))) return null;
    if (type === 'radar' && (series[0].data.length < 3 || series[0].data.length > 12)) return null;
    const pointLabels = series[0].data.map(point => point.x);
    if (series.some(item => item.data.length !== pointLabels.length || item.data.some((point, index) => point.x !== pointLabels[index]))) {
      return null;
    }

    return {
      type,
      title: asTrimmedString(parsed.title),
      subtitle: asTrimmedString(parsed.subtitle, 240),
      note: asTrimmedString(parsed.note, 240),
      xLabel: asTrimmedString(parsed.xLabel, 60),
      yLabel: asTrimmedString(parsed.yLabel, 60),
      yFormat: ['number', 'currency', 'percent'].includes(parsed.yFormat) ? parsed.yFormat : 'number',
      currency: /^[A-Z]{3}$/.test(parsed.currency || '') ? parsed.currency : 'USD',
      series,
    };
  } catch (_) {
    return null;
  }
}

export function formatChartValue(value, spec, compact = true) {
  const options = {
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 2,
  };

  if (spec?.yFormat === 'currency') {
    options.style = 'currency';
    options.currency = spec.currency || 'USD';
    options.maximumFractionDigits = compact ? 1 : 0;
  } else if (spec?.yFormat === 'percent') {
    return `${new Intl.NumberFormat(undefined, options).format(value)}%`;
  }

  return new Intl.NumberFormat(undefined, options).format(value);
}

export function chartSpecToTable(spec) {
  const series = Array.isArray(spec?.series) ? spec.series : [];
  if (series.length === 0) return { headers: [], rows: [] };

  return {
    headers: [spec.xLabel || 'Category', ...series.map(item => item.name)],
    rows: series[0].data.map((point, pointIndex) => [
      point.x,
      ...series.map(item => formatChartValue(item.data[pointIndex].y, spec, false)),
    ]),
  };
}
